import { foodOrigin } from '../lib/food-origin';
import { MealPicker } from '../components/MealPicker';
import { FoodBrandInput } from '../components/FoodBrandInput';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  parseNumericInput,
  formatNumber,
  formatDecimal,
  formatShortDate,
  type Food,
  type FoodEntry,
  type Meal,
} from '@jungles/shared';
import { api } from '../lib/api';
import { displayFoodBrand, foodPortionGrams, foodPortionMl } from '../lib/food-search';
import { useApi } from '../lib/hooks';
import { useToast } from '../app/toast';
import { hapticSuccess } from '../lib/telegram';
import { BackLink, Header, Loader, Screen, TabBar } from '../components/Chrome';

/**
 * N1 · Продукт вручную и N3 · Проверка значений.
 * Явное «Добавить»/«Сохранить» подтверждает показанные значения и порцию.
 */

export interface ScanPrefill {
  name: string;
  foodId?: string;
  brand: string | null;
  grams: number;
  basis: 'per100' | 'perServing';
  kcal: number | null;
  protein: number | null;
  fat: number | null;
  carbs: number | null;
  barcode?: string | null;
  source: 'barcode' | 'ocr' | 'photo';
  barcodeOrigin?: 'lookup' | 'manual' | 'ocr';
  restrictedCandidate?: boolean;
  notes?: string | null;
  nutritionBasis?: Food['nutritionBasis'];
}

type Basis = 'per100' | 'perServing';
type NutrientKey = 'kcal' | 'protein' | 'fat' | 'carbs';
interface NutrientInput {
  text: string;
  value: number;
  basis: Basis;
}
const NUTRIENTS: NutrientKey[] = ['kcal', 'protein', 'fat', 'carbs'];

function nutrient(value: number | null, basis: Basis = 'per100'): NutrientInput {
  return { text: value === null || !Number.isFinite(value) ? '' : String(Number(value.toFixed(1))).replace('.', ','), value: value ?? NaN, basis };
}

function portionNumber(value: number): string {
  return Number.isFinite(value) ? formatNumber(value, 1) : '—';
}

interface FormState {
  name: string;
  brand: string;
  grams: string;
  basis: 'per100' | 'perServing';
  kcal: NutrientInput;
  protein: NutrientInput;
  fat: NutrientInput;
  carbs: NutrientInput;
}

const EMPTY: FormState = {
  name: '',
  brand: '',
  grams: '150',
  basis: 'per100',
  kcal: nutrient(null),
  protein: nutrient(null),
  fat: nutrient(null),
  carbs: nutrient(null),
};

function num(value: string): number {
  return parseNumericInput(value);
}

// Округлённый текст никогда не участвует в расчётах. Переключения считаем
// от последнего ввода, а не от результата предыдущего переключения.
function portionDenominator(form: FormState): number {
  return num(form.grams);
}

function nutrientValue(form: FormState, key: NutrientKey, unverified = false): number {
  const input = form[key];
  if (unverified || input.basis === form.basis) return input.value;
  return input.value * (form.basis === 'perServing' ? portionDenominator(form) / 100 : 100 / portionDenominator(form));
}

function nutrientText(form: FormState, key: NutrientKey, unverified = false): string {
  return unverified || form[key].basis === form.basis ? form[key].text : nutrient(nutrientValue(form, key, unverified)).text;
}

export function FoodFormScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [meal, setMeal] = useState<Meal>((params.get('meal') ?? 'snack') as Meal);
  const date = params.get('date') ?? '';
  const foodId = params.get('foodId');
  const entryId = params.get('entry');

  const selection = location.state as { prefill?: ScanPrefill; food?: Food; catalogSelection?: boolean; portionGrams?: number | null } | null;
  const prefill = entryId ? null : selection?.prefill ?? null;
  const selectedFood = !entryId && selection?.food?.id === foodId ? selection.food : null;

  const { data: food, loading, error: foodError } = useApi<Food>(
    foodId && !entryId && !selectedFood ? `/api/foods/${encodeURIComponent(foodId)}` : null,
  );

  const { data: entry, loading: entryLoading, error: entryError } = useApi<
    FoodEntry & Pick<Food, 'kcal100' | 'p100' | 'f100' | 'c100'>
  >(entryId ? `/api/nutrition/entries/${encodeURIComponent(entryId)}` : null);

  const [form, setForm] = useState<FormState>(() => {
    if (prefill) {
      return {
        name: prefill.name,
        brand: prefill.brand ?? '',
        grams: String(prefill.grams).replace('.', ','),
        basis: prefill.basis,
        kcal: nutrient(prefill.kcal, prefill.basis),
        protein: nutrient(prefill.protein, prefill.basis),
        fat: nutrient(prefill.fat, prefill.basis),
        carbs: nutrient(prefill.carbs, prefill.basis),
      };
    }
    return EMPTY;
  });

  const [barcode, setBarcode] = useState(prefill?.barcode ?? selectedFood?.barcode ?? params.get('barcode') ?? '');
  const sourceBasis = prefill?.nutritionBasis ?? selectedFood?.nutritionBasis ?? food?.nutritionBasis;
  // A new manual/barcode product starts in grams. Existing catalog selections and
  // unknown OCR labels still require their source basis; a known ml label stays ml.
  const newProductDefault = !foodId && !entryId && (!prefill || prefill.source === 'barcode' &&
    (!prefill.foodId || [prefill.kcal, prefill.protein, prefill.fat, prefill.carbs].some(value => value === null)));
  const [unit, setUnit] = useState<'g' | 'ml' | ''>(() => sourceBasis === 'per100ml' ? 'ml'
    : sourceBasis === 'off_100g_physical_basis_unverified' && !newProductDefault ? '' : 'g');
  const changeUnit = (next: 'g' | 'ml') => {
    if (next === unit) return;
    // No density is known: never convert g to ml or silently relabel nutrients.
    setForm(current => ({...current, basis:'per100', ...Object.fromEntries(NUTRIENTS.map(key => [key,nutrient(null)]))}));
    setUnit(next);
  };
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Каталог задаёт исходные значения; правки КБЖУ относятся только к записи.
  const catalogFood = useMemo(() => {
    if (!foodId || entryId) return null;
    return selectedFood ?? (food?.id === foodId ? food : null);
  }, [food, foodId, selectedFood, entryId]);

  const picked = catalogFood;
  const portionMl = picked ? foodPortionMl(picked) : null;
  const portionGrams = picked ? foodPortionGrams(picked)
    : !entryId && selection?.catalogSelection ? foodPortionGrams({ defaultGrams: selection.portionGrams ?? undefined }) : null;

  useEffect(() => {
    if (!picked || entryId) return;
    setBarcode(params.get('barcode') || picked.barcode || '');
    setUnit(picked.nutritionBasis === 'off_100g_physical_basis_unverified' ? '' : picked.nutritionBasis === 'per100ml' ? 'ml' : 'g');
    setForm((current) => ({
      ...current,
      name: picked.name,
      brand: picked.brand ?? '',
      grams: String(foodPortionMl(picked) ?? foodPortionGrams(picked) ?? 100).replace('.', ','),
      basis: 'per100',
      kcal: nutrient(picked.kcal100),
      protein: nutrient(picked.p100),
      fat: nutrient(picked.f100),
      carbs: nutrient(picked.c100),
    }));
  }, [picked, entryId]);

  useEffect(() => {
    if (!entry || entry.id !== entryId) return;
    setMeal(entry.meal);
    setUnit(entry.quantityUnit ?? 'g');
    setForm({
      name: entry.name, brand: entry.brand ?? '', grams: String(entry.grams).replace('.', ','),
      basis: 'per100', kcal: nutrient(entry.kcal100), protein: nutrient(entry.p100),
      fat: nutrient(entry.f100), carbs: nutrient(entry.c100),
    });
  }, [entry, entryId]);

  const update = (patch: Partial<FormState>) => {
    setForm((current) => {
      const next = { ...current, ...patch };
      if (patch.grams !== undefined) {
        // Вес меняет порцию, но не уже введённые КБЖУ выбранного базиса.
        for (const key of NUTRIENTS) {
          if (current[key].basis !== current.basis) {
            next[key] = { text: nutrientText(current, key), value: nutrientValue(current, key), basis: current.basis };
          }
        }
      }
      return next;
    });
  };

  const updateNutrient = (key: NutrientKey, text: string) => {
    setForm((current) => {
      const input = { text, value: num(text), basis: current.basis };
      return { ...current, [key]: input };
    });
  };

  const grams = num(form.grams);
  const factor = form.basis === 'per100' ? grams / 100 : grams / portionDenominator(form);
  const portionKcal = nutrientValue(form, 'kcal') * factor;
  // Compare effective per-100 values, retaining exact source values on unchanged roundtrips.
  const snapshot = entryId ? entry : picked;
  const sourceUnit = entryId ? entry?.quantityUnit ?? 'g' : sourceBasis === 'per100ml' ? 'ml' : 'g';
  const nutrientsEdited = snapshot ? unit !== sourceUnit || NUTRIENTS.some((key) => {
    const original = { kcal: snapshot.kcal100, protein: snapshot.p100, fat: snapshot.f100, carbs: snapshot.c100 }[key];
    const value = nutrientValue(form, key) * (form.basis === 'perServing' ? 100 / portionDenominator(form) : 1);
    return !Number.isFinite(value) || Math.abs(value - original) > Number.EPSILON * Math.max(1, Math.abs(value), Math.abs(original)) * 8;
  }) : true;

  const validGrams = Number.isFinite(grams) && grams >= 1 && grams <= 5000;
  const validNutrients = NUTRIENTS.every((key) => {
    const value = nutrientValue(form, key);
    const limit = key === 'kcal' ? 10000 : 1000;
    const per100 = value * (form.basis === 'perServing' ? 100 / portionDenominator(form) : 1);
    return Number.isFinite(value) && value >= 0 && value <= limit && per100 <= limit;
  });
  const canSubmit =
    (!foodId || Boolean(picked) && !loading && !foodError) &&
    (!entryId || (entry?.id === entryId && !entryLoading && !entryError)) &&
    form.name.trim().length > 0 &&
    validGrams &&
    (!barcode || /^\d{8,14}$/.test(barcode)) &&
    Boolean(unit) && validNutrients && form.name.trim().length <= 120 && form.brand.trim().length <= 80;

  const validationError = attempted && !canSubmit ? !unit ? 'Уточните единицу КБЖУ на упаковке' : !validNutrients ? 'Заполните все поля КБЖУ' : 'Проверьте поля продукта и вес' : null;

  const submit = async () => {
    if (busy) return;
    setAttempted(true);
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const nutrientOverride = {
        basis: form.basis,
        kcal: nutrientValue(form, 'kcal'), protein: nutrientValue(form, 'protein'),
        fat: nutrientValue(form, 'fat'), carbs: nutrientValue(form, 'carbs'),
      };
      if (entryId) {
        await api.patch(`/api/nutrition/entries/${entryId}`, { grams, quantityUnit: unit, meal, ...(nutrientsEdited ? { nutrientOverride } : {}) });
      } else if (picked || prefill?.foodId) {
        const selectedId = picked?.id ?? prefill!.foodId!;
        await api.post('/api/nutrition/entries', { date: date || undefined, meal, grams, quantityUnit: unit, foodId: selectedId,
          ...(nutrientsEdited || !picked || selectedId.startsWith('off:') ? { nutrientOverride } : {}),
          ...(selectedId.startsWith('off:') && prefill ? { food: { name: form.name.trim(), brand: form.brand.trim() || null, ...nutrientOverride } } : {}) });
      } else {
        await api.post('/api/nutrition/entries', {
          date: date || undefined,
          meal,
          grams,
          quantityUnit: unit,
          publicContribution: prefill?.source !== 'photo',
          food: {
            name: form.name.trim(),
            brand: form.brand.trim() || null,
            barcode: barcode || null,
            kcal: nutrientValue(form, 'kcal'),
            protein: nutrientValue(form, 'protein'),
            fat: nutrientValue(form, 'fat'),
            carbs: nutrientValue(form, 'carbs'),
            basis: form.basis,
          },
        });
      }
      hapticSuccess();
      toast(entryId ? 'Запись сохранена' : 'Добавили в дневник');
      const origin = !entryId ? foodOrigin(location.state) : null;
      navigate(origin?.path ?? `/nutrition?date=${entry?.date ?? date}`, {
        replace: true, state: origin ? { restoreFoodOrigin: origin } : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось добавить');
      setBusy(false);
    }
  };

  const removeEntry = async () => {
    if (!entryId || entry?.id !== entryId || entryLoading || entryError || busy) return;
    if (!window.confirm(`Удалить «${entry.name}» из дневника?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/api/nutrition/entries/${encodeURIComponent(entryId)}`);
      toast('Запись удалена');
      navigate(`/nutrition?date=${entry.date}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
      setBusy(false);
    }
  };

  if ((loading && foodId) || (entryLoading && entryId)) {
    return (
      <Screen tabs>
        <Header />
        <Loader />
        <TabBar />
      </Screen>
    );
  }

  const readOnlyIdentity = Boolean(prefill?.restrictedCandidate && prefill.foodId) || Boolean(picked) || Boolean(entryId) || Boolean(selection?.catalogSelection && prefill);

  return (
    <Screen tabs>
      <Header />
      <BackLink to={foodOrigin(location.state) ? -1 : `/nutrition?date=${entry?.date ?? date}`} label="Назад" />
      <h1>
        {entryId ? 'Изменить запись' : prefill ? 'Проверьте значения' : 'Добавить продукт'}
      </h1>
      <span className="ctx food-context">
        <MealPicker value={meal} onChange={setMeal} /> · {entry?.date || date ? formatShortDate(entry?.date ?? date) : 'сегодня'}
      </span>

      <section className="card">
        <h2>Продукт и порция</h2>
        {prefill?.restrictedCandidate ? <p className="nt" role="note">Магазинный код: это возможное совпадение. Проверьте продукт и КБЖУ на упаковке.{!prefill.foodId ? ' Новый продукт сохранится в общем каталоге.' : null}</p> : null}
        {prefill?.source === 'photo' ? <p className="nt" role="note"><b>Примерная оценка по фото.</b> Проверьте название, вес и КБЖУ. {prefill.notes} Запись появится только после «Добавить».</p> : null}
        {prefill?.source === 'ocr' && prefill.notes ? <p className="nt">{prefill.notes}</p> : null}

        {readOnlyIdentity ? (
          <dl className="food-identity">
            <dt>Название</dt>
            <dd>{form.name}</dd>
            <dt>Бренд</dt>
            <dd>{displayFoodBrand(form.brand) || 'Не указан'}</dd>
            {portionGrams !== null || portionMl !== null ? <>
              <dt>Порция</dt>
              <dd>{formatDecimal((portionMl ?? portionGrams)!)} {portionMl !== null ? 'мл' : 'г'}</dd>
            </> : null}
          </dl>
        ) : (
          <>
            <label className="fl">
              Название
              <input
                className="f"
                value={form.name}
                onChange={(event) => update({ name: event.target.value })}
                placeholder="Например, творог 5%"
              />
            </label>
            <FoodBrandInput value={form.brand} onChange={brand => update({brand})} />
          </>
        )}

        {barcode && (prefill?.barcodeOrigin === 'lookup' || picked) ? <p className="nt">Штрихкод: <span className="code food-barcode">{barcode}</span></p> : prefill?.barcode || prefill?.source === 'ocr' ? (
          <label className="fl">Штрихкод<input className="f" inputMode="numeric" value={barcode} onChange={e => setBarcode(e.target.value)} /></label>
        ) : null}

        {sourceBasis === 'off_100g_physical_basis_unverified' && !entryId ? (
          <div className="perrow">
            <span className="seglbl" id="unit-label">Единица КБЖУ</span>
            <div className="seg" role="radiogroup" aria-labelledby="unit-label" aria-required="true">
              {(['g', 'ml'] as const).map(value => <button key={value} type="button" role="radio"
                aria-checked={unit === value} className={unit === value ? 'on' : undefined}
                onClick={() => newProductDefault ? changeUnit(value) : setUnit(value)}>{value === 'g' ? 'г' : 'мл'}</button>)}
            </div>
          </div>
        ) : !foodId && !prefill?.foodId && !entryId && sourceBasis !== 'per100ml' && sourceBasis !== 'per100g' ? <div className="perrow">
          <span className="seglbl" id="unit-label">Единица</span>
          <div className="seg" role="radiogroup" aria-labelledby="unit-label">
            {(['g', 'ml'] as const).map(value => <button key={value} type="button" role="radio"
              aria-checked={unit === value} className={unit === value ? 'on' : undefined}
              onClick={() => changeUnit(value)}>{value === 'g' ? 'г' : 'мл'}</button>)}
          </div>
        </div> : <p className="nt">Единица: {unit === 'ml' ? 'мл' : 'г'}</p>}
        <label className="fl">
          {unit === 'ml' ? 'Объём, мл' : unit === 'g' ? 'Вес, г' : 'Количество'}
          <input
            className="f"
            type="text"
            inputMode="decimal"
            value={form.grams}
            onChange={(event) => update({ grams: event.target.value })}
          />
        </label>


        <>
            {prefill?.foodId && [prefill.kcal, prefill.protein, prefill.fat, prefill.carbs].some(value => value === null) ? <p className="nt">Заполненные КБЖУ сохранятся в общем каталоге.</p> : picked || prefill?.foodId || entryId ? <p className="nt">Изменения КБЖУ сохраняются только в этой записи дневника.</p> : null}
            <div className="perrow">
              <span className="seglbl" id="basis-label">
                КБЖУ указаны
              </span>
              <div className="seg" role="radiogroup" aria-labelledby="basis-label">
                <button
                  type="button"
                  role="radio"
                  aria-checked={form.basis === 'per100'}
                  disabled={!validGrams}
                  className={form.basis === 'per100' ? 'on' : undefined}
                  onClick={() => update({ basis: 'per100' })}
                >
                  {unit === 'ml' ? 'на 100 мл' : unit === 'g' ? 'на 100 г' : 'на 100'}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={form.basis === 'perServing'}
                  disabled={!validGrams}
                  className={form.basis === 'perServing' ? 'on' : undefined}
                  onClick={() => update({ basis: 'perServing' })}
                >
                  на порцию
                </button>
              </div>
            </div>

            <div className="fields">
              <label className="fl">
                Калории, ккал
                <input
                  className="f"
                  type="text"
                  inputMode="decimal"
                  aria-label="Калории, ккал"
                  aria-invalid={attempted && !Number.isFinite(nutrientValue(form, 'kcal')) || undefined}
                  aria-describedby={attempted && !Number.isFinite(nutrientValue(form, 'kcal')) ? 'kcal-error' : undefined}
                  value={nutrientText(form, 'kcal')}
                  onChange={(event) => updateNutrient('kcal', event.target.value)}
                />
                {attempted && !Number.isFinite(nutrientValue(form, 'kcal')) ? <span className="err" id="kcal-error">Введите значение</span> : null}
              </label>
              <label className="fl">
                Белки, г
                <input
                  className="f"
                  type="text"
                  inputMode="decimal"
                  aria-label="Белки, г"
                  aria-invalid={attempted && !Number.isFinite(nutrientValue(form, 'protein')) || undefined}
                  aria-describedby={attempted && !Number.isFinite(nutrientValue(form, 'protein')) ? 'protein-error' : undefined}
                  value={nutrientText(form, 'protein')}
                  onChange={(event) => updateNutrient('protein', event.target.value)}
                />
                {attempted && !Number.isFinite(nutrientValue(form, 'protein')) ? <span className="err" id="protein-error">Введите значение</span> : null}
              </label>
              <label className="fl">
                Жиры, г
                <input
                  className="f"
                  type="text"
                  inputMode="decimal"
                  aria-label="Жиры, г"
                  aria-invalid={attempted && !Number.isFinite(nutrientValue(form, 'fat')) || undefined}
                  aria-describedby={attempted && !Number.isFinite(nutrientValue(form, 'fat')) ? 'fat-error' : undefined}
                  value={nutrientText(form, 'fat')}
                  onChange={(event) => updateNutrient('fat', event.target.value)}
                />
                {attempted && !Number.isFinite(nutrientValue(form, 'fat')) ? <span className="err" id="fat-error">Введите значение</span> : null}
              </label>
              <label className="fl">
                Углеводы, г
                <input
                  className="f"
                  type="text"
                  inputMode="decimal"
                  aria-label="Углеводы, г"
                  aria-invalid={attempted && !Number.isFinite(nutrientValue(form, 'carbs')) || undefined}
                  aria-describedby={attempted && !Number.isFinite(nutrientValue(form, 'carbs')) ? 'carbs-error' : undefined}
                  value={nutrientText(form, 'carbs')}
                  onChange={(event) => updateNutrient('carbs', event.target.value)}
                />
                {attempted && !Number.isFinite(nutrientValue(form, 'carbs')) ? <span className="err" id="carbs-error">Введите значение</span> : null}
              </label>
            </div>
        </>

        <div className="ins">
          {'Съедено ' + portionNumber(grams) + (unit === 'ml' ? ' мл: ' : unit === 'g' ? ' г: ' : ' единиц: ') + portionNumber(portionKcal)} ккал · Белки {portionNumber(nutrientValue(form, 'protein') * factor)} г
          {' · '}Жиры {portionNumber(nutrientValue(form, 'fat') * factor)} г
          {' · '}Углеводы {portionNumber(nutrientValue(form, 'carbs') * factor)} г
        </div>
        {entryError || foodError || error || validationError ? <p className="err" role="alert">{entryError ?? foodError ?? error ?? validationError}</p> : null}

        <div className="acts">
          <button
            className="b p big"
            type="button"
            onClick={() => void submit()}
            disabled={busy || !validGrams || !form.name.trim() || Boolean(barcode && !/^\d{8,14}$/.test(barcode))}
          >
            {entryId ? 'Сохранить' : 'Добавить'}
          </button>
        </div>
        {entryId ? (
          <button className="b d" type="button" style={{ marginTop: 12 }}
            disabled={busy || entryLoading || Boolean(entryError) || entry?.id !== entryId}
            onClick={() => void removeEntry()}>
            Удалить запись
          </button>
        ) : null}
      </section>

      <TabBar />
    </Screen>
  );
}
